/**
 * Feature-Flag-Modul für ET
 *
 * Alle Phase-1..4-UI-Bausteine sind seit 2026-07-22 dauerhaft in Preview UND
 * Produktion aktiv (kein Env-Gating mehr). Overrides via URL-Query oder
 * localStorage bleiben für Support-Debugging bestehen.
 *
 * Priorität (höchste zuerst):
 *   1. URL-Query-Parameter:      ?ff.step3AutoRecalc=1
 *   2. localStorage:             localStorage.setItem("ff.step3AutoRecalc", "1")
 *   3. Default:                  DEFAULTS[key]
 */

const FLAG_KEYS = [
  "step3AutoRecalc", // Phase 1 — Debounced Backend-Recalc
  "step3CompactRow", // Phase 2 — Kompakt-Zeile + Detail-Drawer
  "step3Timeline",   // Phase 3 — Zeitleisten-Widget
  "step3NewTerms",   // Phase 4 — Fachsprache + ruhige Farben
  "step3BulkActions", // Phase 5 — Multi-Select-Aktionen
];

// Defaults für alle Umgebungen (Preview + Produktion). Phase 1..4 sind
// dauerhaft aktiv; step3BulkActions bleibt aus (auf User-Wunsch aus dem UI
// entfernt, Code bleibt für zukünftige Wiederaktivierung erhalten).
const DEFAULTS = {
  step3AutoRecalc: true,
  step3CompactRow: true,
  step3Timeline: true,
  step3NewTerms: true,
  step3BulkActions: false,
};

function readQueryParam(key) {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const v = params.get(`ff.${key}`);
  if (v === null) return null;
  return v === "1" || v === "true" || v === "on";
}

function readLocalStorage(key) {
  try {
    const v = window.localStorage.getItem(`ff.${key}`);
    if (v === null) return null;
    return v === "1" || v === "true" || v === "on";
  } catch (_e) {
    return null;
  }
}

/**
 * Prüft ob ein Feature aktiv ist.
 * @param {string} key eines der FLAG_KEYS
 * @returns {boolean}
 */
export function isFeatureEnabled(key) {
  if (!FLAG_KEYS.includes(key)) {
    // Unbekannte Flags werden defensiv als aus interpretiert.
    return false;
  }
  const q = readQueryParam(key);
  if (q !== null) return q;
  const ls = readLocalStorage(key);
  if (ls !== null) return ls;
  return DEFAULTS[key] ?? false;
}

/**
 * Setzt einen Flag zur Laufzeit (persistiert in localStorage). Nur für
 * Entwickler-/Support-Tools gedacht. `null` löscht den Overwrite.
 */
export function setFeatureFlag(key, value) {
  if (!FLAG_KEYS.includes(key)) {
    console.warn(`[features] unknown flag: ${key}`);
    return;
  }
  try {
    if (value === null) {
      window.localStorage.removeItem(`ff.${key}`);
    } else {
      window.localStorage.setItem(`ff.${key}`, value ? "1" : "0");
    }
  } catch (_e) {
    /* noop */
  }
}

/**
 * Für Debug / Support: gibt den effektiven Status aller Flags zurück.
 */
export function getAllFeatures() {
  const out = {};
  for (const k of FLAG_KEYS) out[k] = isFeatureEnabled(k);
  return out;
}

// Für Devtools-Zugriff im Browser: window.__ET_FEATURES__
if (typeof window !== "undefined") {
  window.__ET_FEATURES__ = {
    get: getAllFeatures,
    set: setFeatureFlag,
    keys: FLAG_KEYS,
  };
}

export { FLAG_KEYS };
